import { getStatusEffect } from './statusEffects.js';
import { consumableRestoreAmount } from '../consumables/procedural.js';

const STAT_LABELS: Record<string, string> = {
  FOR: 'FOR', DEX: 'DEX', INT: 'INT', SAG: 'SAG', CHA: 'CHA',
  VIT: 'VIT', ESP: 'ESP', END: 'END', AGI: 'AGI',
};

const TARGET_LABELS: Record<string, string> = {
  self: 'une cible vivante',
  single: 'une cible vivante',
  ally: 'une cible vivante',
  allAllies: 'tous les combattants vivants du camp ciblé',
  allEnemies: 'tous les combattants vivants du camp ciblé',
  all: 'tous les combattants vivants du camp ciblé',
};

const ELEMENT_LABELS: Record<string, string> = {
  physical: 'physiques', fire: 'de Feu', ice: 'de Glace', lightning: 'de Foudre',
  water: "d'Eau", earth: 'de Terre', wind: 'de Vent', nature: 'de Nature',
  holy: 'Sacrés', dark: 'de Ténèbres',
};

export interface ConsumableDescriptionOptions {
  resolveSkill?: (skillId: string) => any;
  stats?: Record<string, number>;
  target?: any;
  compact?: boolean;
}

export function describeFreeTarget(skillOrEffect: any) {
  if (skillOrEffect?.type === 'revive' || skillOrEffect?.effect === 'revive') {
    return 'Ciblage libre · n’importe quel combattant KO';
  }
  return `Ciblage libre · ${TARGET_LABELS[skillOrEffect?.target] || 'une cible vivante'}`;
}

export function formatAffinity(affinity: Record<string, number> = {}) {
  const entries = Object.entries(affinity).filter(([, value]) => Number(value));
  if (!entries.length) return 'FOR + 0,3 × DEX';
  return entries
    .map(([stat, value]) => `${Number(value).toLocaleString('fr-FR')} × ${STAT_LABELS[stat] || stat}`)
    .join(' + ');
}

function basePower(skill: any, stats?: Record<string, number>) {
  if (!stats) return null;
  const affinity = skill?.affinity || {};
  const entries = Object.entries(affinity);
  if (!entries.length) return Number(stats.FOR || 10) + Number(stats.DEX || 10) * 0.3;
  return entries.reduce((sum, [stat, multiplier]) => sum + Number(stats[stat] || 0) * Number(multiplier || 0), 0);
}

function powerRange(power: number, multiplier = 1) {
  const value = power * multiplier;
  return `${Math.floor(value * 0.9)}–${Math.floor(value * 1.1)}`;
}

function describeStatus(status: any, compact = false) {
  const def: any = getStatusEffect(status.id);
  if (!def) return null;
  const effects: string[] = [];
  if (def.dot?.pctMaxHp) {
    const dotElement = ELEMENT_LABELS[def.dot.element] || def.dot.element || '';
    effects.push(compact
      ? `${Math.round(def.dot.pctMaxHp * 100)} % PV max ${dotElement}/tour`
      : `${Math.round(def.dot.pctMaxHp * 100)} % des PV max de dégâts ${dotElement}/tour`);
  }
  if (def.hot?.pctMaxHp) effects.push(compact ? `+${Math.round(def.hot.pctMaxHp * 100)} % PV max/tour` : `rend ${Math.round(def.hot.pctMaxHp * 100)} % des PV max/tour`);
  if (def.skipTurn) effects.push(compact ? 'tour sauté' : 'fait sauter le tour');
  if (def.breakOnDamage) effects.push(compact ? 'rompu aux dégâts' : 'rompu au prochain dégât');
  if (def.charm) effects.push(compact ? 'change de camp' : 'retourne la cible contre son camp');
  for (const [stat, value] of Object.entries(def.statMod || {})) {
    const label = stat === 'degats' ? 'dégâts' : stat === 'armure' ? 'armure' : stat === 'VIT' ? (compact ? 'VIT' : 'vitesse de combat') : stat;
    effects.push(`${Number(value) >= 0 ? '+' : ''}${Math.round(Number(value) * 100)} % ${label}`);
  }
  for (const [element, value] of Object.entries(def.affinityMod || {})) {
    effects.push(`+${Math.round(Number(value) * 50)} % dégâts ${element === 'all' ? (compact ? 'tous éléments' : 'de tous les éléments') : ELEMENT_LABELS[element] || element}`);
  }
  for (const [element, value] of Object.entries(def.resistMod || {})) {
    const amount = Number(value);
    const reduction = Math.round((1 - 100 / (100 + Math.abs(amount) * 5)) * 100);
    const label = element === 'all' ? 'tous éléments' : ELEMENT_LABELS[element] || element;
    effects.push(amount > 0
      ? `${amount >= 0 ? '+' : ''}${amount} ${compact ? 'rés.' : 'résistance'} ${label} (≈ −${reduction} % ${compact ? 'subis' : 'de ces dégâts subis'})`
      : `${amount} ${compact ? 'rés.' : 'résistance'} ${label} (≈ +${reduction} % ${compact ? 'subis' : 'de ces dégâts subis'})`);
  }
  if (def.counter) effects.push(`renvoie ${Math.round(def.counter.pct * 100)} % des dégâts ${def.counter.kind === 'all' ? 'reçus' : def.counter.kind === 'magical' ? 'magiques reçus' : 'physiques reçus'}`);
  if (def.shield?.flat) effects.push(compact ? `barrière ${def.shield.flat}` : `absorbe exactement ${def.shield.flat} dégâts`);
  if (def.revive) effects.push(compact ? `retour à ${Math.round(def.revive.pctHp * 100)} % PV max si KO` : `ressuscite à ${Math.round(def.revive.pctHp * 100)} % des PV max si KO`);
  if (compact && def.ignoresBarrier) effects.push('ignore la barrière');
  const chance = compact
    ? `${Math.round(Number(status.chance == null ? 1 : status.chance) * 100)} %`
    : status.chance == null || status.chance >= 1 ? 'garanti' : `${Math.round(status.chance * 100)} %`;
  const duration = status.duration && status.duration < 99
    ? compact ? `${status.duration} t.` : `${status.duration} tour${status.duration > 1 ? 's' : ''}`
    : compact ? 'jusqu’au déclenchement' : 'jusqu’à déclenchement';
  return `${def.name} : ${effects.join(', ') || def.desc} · ${chance} · ${duration}`;
}
export function describeStatusEntry(status: any) {
  const def: any = getStatusEffect(status.id);
  const line = describeStatus(status);
  if (!def || !line) return null;
  return { icon: def.icon, name: def.name, text: line.slice(`${def.name} : `.length) };
}

export function describeSkillStatuses(skill: any) {
  return (skill?.statuses || []).map(describeStatusEntry).filter(Boolean);
}

function describeSkillEffectLines(skill: any, stats?: Record<string, number>, compact = false) {
  const lines: string[] = [];
  const power = basePower(skill, stats);
  const formula = formatAffinity(skill.affinity || {});
  const element = ELEMENT_LABELS[skill.element] || skill.element || 'physiques';

  if (['attack', 'aoe'].includes(skill.type)) {
    lines.push(compact
      ? power == null
        ? `Dégâts ${element} : ${formula} (±10 %, avant défense/éléments)`
        : `${powerRange(power)} dégâts ${element} (${formula}, avant défense/éléments)`
      : `Dégâts ${element} : ${power == null ? `puissance ${formula} (±10 % avant défense/éléments)` : `${powerRange(power)} avant défense/éléments`} · formule ${formula}`);
  } else if (skill.type === 'dot') {
    lines.push(compact
      ? `Impact ${element} : ${power == null ? `50 % de ${formula}` : powerRange(power, 0.5)} avant effets`
      : `Impact direct ${element} : 50 % de la puissance ${formula}${power == null ? '' : ` (${powerRange(power, 0.5)})`}`);
  } else if (skill.type === 'heal') {
    lines.push(compact
      ? power == null ? `Soin : ${formula} (±10 %)` : `Soin : ${powerRange(power)} PV (${formula})`
      : `Soin : ${power == null ? `puissance ${formula} (±10 %)` : `${powerRange(power)} PV`} · formule ${formula}`);
  } else if (skill.type === 'barrier') {
    const flat = Number(skill.effect?.flat ?? 50);
    const amount = stats ? flat + Math.floor(Number(stats.SAG || 0) * 0.5) : null;
    lines.push(compact
      ? `Barrière : ${amount == null ? `${flat} + 0,5 × SAG` : amount} dégâts · 3 t.`
      : `Barrière : ${amount == null ? `${flat} + 0,5 × SAG` : `${amount} dégâts absorbés`} · dure 3 tours`);
  } else if (skill.type === 'revive') {
    lines.push(compact
      ? `Résurrection : ${Math.round(Number(skill.effect?.pctHp ?? 0.35) * 100)} % PV max`
      : `Résurrection : ${Math.round(Number(skill.effect?.pctHp ?? 0.35) * 100)} % des PV max`);
  }
  return lines;
}

function describeSkillCostLines(skill: any, compact = false) {
  const costs = [skill.cost?.mp ? `${skill.cost.mp} PM` : '', skill.cost?.stamina ? `${skill.cost.stamina} ${compact ? 'End.' : 'Endurance'}` : ''].filter(Boolean);
  const lines: string[] = [];
  if (costs.length) lines.push(compact ? costs.join(' + ') : `Coût : ${costs.join(' + ')}`);
  if (skill.cooldown) lines.push(compact ? `Recharge ${skill.cooldown} t.` : `Recharge : ${skill.cooldown} tour${skill.cooldown > 1 ? 's' : ''}`);
  return lines;
}

export function describeSkillMechanics(skill: any, stats?: Record<string, number>, includeStatuses = true) {
  if (!skill) return [];
  const lines: string[] = [describeFreeTarget(skill), ...describeSkillEffectLines(skill, stats)];
  if (includeStatuses) {
    for (const status of skill.statuses || []) {
      const line = describeStatus(status);
      if (line) lines.push(line);
    }
  }
  lines.push(...describeSkillCostLines(skill));
  return lines;
}

export interface CombatSkillDescriptionOptions {
  includeIntro?: boolean;
  includeTarget?: boolean;
  includeCosts?: boolean;
}

/** Résumé de combat calculé depuis la définition canonique, sans second texte à maintenir. */
export function describeCombatSkill(
  skill: any,
  stats?: Record<string, number>,
  options: CombatSkillDescriptionOptions = {},
) {
  if (!skill) return '';
  const { includeIntro = true, includeTarget = false, includeCosts = false } = options;
  const lines: string[] = [];
  if (includeTarget) lines.push(describeFreeTarget(skill));
  const intro = String(skill.desc || skill.description || '').replace(/\s+/g, ' ').trim().replace(/[.!?]+$/, '');
  if (includeIntro && intro) lines.push(intro);
  lines.push(...describeSkillEffectLines(skill, stats, true));
  for (const status of skill.statuses || []) {
    const line = describeStatus(status, true);
    if (line) lines.push(line);
  }
  if (includeCosts) lines.push(...describeSkillCostLines(skill, true));
  return lines.join(' · ');
}

function describeRecoverable(combat: any, target: any) {
  if (!target) return '';
  const value = Number(combat.value ?? 0);
  const name = target.name || 'la cible sélectionnée';
  if (combat.effect === 'heal' && Number.isFinite(target.hp) && Number.isFinite(target.maxHp)) {
    const actual = Math.max(0, Math.min(value, target.maxHp - target.hp));
    return `Sur ${name} : ${actual} PV récupérables · sursoin ${Math.max(0, value - actual)} PV`;
  }
  if (combat.effect === 'mp' && Number.isFinite(target.mp) && Number.isFinite(target.maxMp)) {
    const actual = Math.max(0, Math.min(value, target.maxMp - target.mp));
    return `Sur ${name} : ${actual} PM récupérables · excédent ${Math.max(0, value - actual)} PM`;
  }
  if (combat.effect === 'stamina' && Number.isFinite(target.stamina) && Number.isFinite(target.maxStamina)) {
    const actual = Math.max(0, Math.min(value, target.maxStamina - target.stamina));
    return `Sur ${name} : ${actual} Endurance récupérable · excédent ${Math.max(0, value - actual)}`;
  }
  if (combat.effect === 'revive' && Number.isFinite(target.maxHp)) return `Sur ${name} : ${Math.floor(target.maxHp * value)} PV au retour`;
  return '';
}

function describeProceduralConsumable(item: any, target?: any) {
  const batch = item?.batch;
  if (!batch?.effects?.length) return '';
  const rarityLabels: Record<string, string> = { common: 'commun', rare: 'rare', epic: 'épique', legendary: 'légendaire' };
  const lines = [
    `Niveau d’objet ${batch.itemLevel} · ${rarityLabels[batch.rarity] || batch.rarity} · ${batch.qualityStars}★ · certification ${batch.requiredCertification}`,
    'Ciblage libre · n’importe quel combattant vivant',
  ];
  for (const effect of batch.effects) {
    if (effect.kind === 'restore') {
      const resource = effect.resource === 'hp' ? 'PV' : effect.resource === 'mp' ? 'PM' : 'Endurance';
      const pct = Math.round(Number(effect.pctMax || 0) * 100);
      const formula = `${Number(effect.flat || 0)} + ${pct} % du maximum ${resource}`;
      if (!target) {
        lines.push(`Restauration ${resource} : ${formula}`);
        continue;
      }
      const total = consumableRestoreAmount(effect, target);
      const current = effect.resource === 'hp' ? Number(target.hp || 0)
        : effect.resource === 'mp' ? Number(target.mp || 0) : Number(target.stamina || 0);
      const maximum = effect.resource === 'hp' ? Number(target.maxHp || 0)
        : effect.resource === 'mp' ? Number(target.maxMp || 0) : Number(target.maxStamina || 0);
      const actual = Math.max(0, Math.min(total, maximum - current));
      lines.push(`Restauration ${resource} : ${formula} = ${total} · récupérable ${actual} · excédent ${Math.max(0, total - actual)}`);
    } else if (effect.kind === 'barrier') {
      lines.push(`Barrière : absorbe exactement ${effect.value} dégâts · ${effect.duration || 3} tours`);
    } else if (effect.kind === 'regen') {
      lines.push(`Régénération : ${Math.round(Number(effect.pctMax || 0) * 100)} % des PV max/tour · ${effect.duration || 2} tours`);
    } else if (effect.kind === 'cleanse') {
      lines.push(`Purification : retire ${effect.count || 1} altération${Number(effect.count || 1) > 1 ? 's' : ''} négative${Number(effect.count || 1) > 1 ? 's' : ''}`);
    } else if (effect.kind === 'status' && effect.statusId) {
      const statusLine = describeStatus({ id: effect.statusId, chance: 1, duration: effect.duration || 2 });
      if (statusLine) lines.push(statusLine);
    }
  }
  return lines.join(' · ');
}
export function describeConsumableEffect(item: any, options: ConsumableDescriptionOptions = {}) {
  if (item?.batch) return describeProceduralConsumable(item, options.target);
  const combat = item?.combat;
  if (!combat) return item?.desc || '';
  const value = Number(combat.value ?? 0);
  const detail = describeRecoverable(combat, options.target);
  const complete = (text: string) => detail ? `${text} · ${detail}` : text;
  if (combat.effect === 'heal') return complete(`Restaure exactement ${value} PV · ciblage libre sur un combattant vivant`);
  if (combat.effect === 'mp') return complete(`Restaure exactement ${value} PM · ciblage libre sur un combattant vivant`);
  if (combat.effect === 'stamina') return complete(`Restaure exactement ${value} Endurance · ciblage libre sur un combattant vivant`);
  if (combat.effect === 'revive') return complete(`Ressuscite n’importe quel combattant KO avec ${Math.round(value * 100)} % de ses PV max`);
  if (combat.effect === 'skill') {
    const skill = combat.skillId ? options.resolveSkill?.(combat.skillId) : null;
    if (!skill) return `Lance la compétence inscrite (${combat.skillId || 'ID manquant'}) sans coût ni recharge · ciblage libre`;
    if (options.compact) {
      const summary = describeCombatSkill({ ...skill, cost: {}, cooldown: 0 }, options.stats, { includeTarget: true });
      return [`Lance ${skill.name} sans coût ni recharge`, summary].filter(Boolean).join(' · ');
    }
    return [`Lance ${skill.name} sans coût ni recharge`, ...describeSkillMechanics({ ...skill, cost: {}, cooldown: 0 }, options.stats, true)].join(' · ');
  }
  return item?.desc || 'Effet spécial.';
}
